<template>
  <fieldset>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Ride Status: </label>
      <span class="col-sm-9 col-8 text-capitalize mobile-pl-0">
        <b>{{ startCase(rideInfo?.status || lastRideHistory.status) }}</b>
      </span>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Ordered On: </label>
      <span class="col-sm-9 col-8 mobile-pl-0">
        <b>{{ rideStatus.createdAt }}</b>
      </span>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Name: </label>
      <span class="col-sm-9 col-8 mobile-pl-0">{{ rideStatus.driverName }}</span>
    </div>
    <div class="row order-ride-info align-items-center">
      <label class="col-sm-3 col-4 mb-0">ETA: </label>
      <span class="col-sm-6 col-5 mobile-pl-0">{{ etaTxt }}</span>
      <div
        v-if="isActiveRide"
        class="col-sm-3 col-3"
      >
        <el-tooltip
          class="item"
          effect="dark"
          content="Refresh ETA"
          placement="top"
        >
          <button
            type="button"
            class="action-btn "
            :disabled="isLoadingRideInfo"
            @click.prevent="onRefresh"
          >
            <i class="fa fa-refresh" />
          </button>
        </el-tooltip>
      </div>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Pick Up: </label>
      <span class="col-sm-9 col-8 mobile-pl-0">{{ rideStatus.startLocation }}</span>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Drop Off: </label>
      <span class="col-sm-9 col-8 mobile-pl-0">{{ rideStatus.endLocation }}</span>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Map: </label>
      <a
      class="col-sm-9 col-8 mobile-pl-0"
      href="#"
      @click="gotoRichMap"
      >
        View Map
      </a>
      <label class="col-sm-3 col-4"/>
      <a
        target="_blank"
        class="col-sm-9 col-8 mobile-pl-0"
        :href="rideStatus.mapUrl"
      >
        View Old GoGo Map
      </a>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Driver Phone Number: </label>
      <span class="col-sm-9 col-8 mobile-pl-0 text-capitalize">
        {{ rideStatus.driverPhone }}
      </span>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Car: </label>
      <span class="col-sm-9 col-8 mobile-pl-0 text-capitalize">
        {{ rideStatus.car }}
      </span>
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">Car Image: </label>
      <img
        class="col-sm-5 col-6 mobile-pl-0"
        :src="rideStatus.carImage ? rideStatus.carImage : '/static/img/no_car.png'"
      >
    </div>
    <div class="row order-ride-info">
      <label class="col-sm-3 col-4">License Plate: </label>
      <span class="col-sm-9 col-8 mobile-pl-0">{{ rideStatus.driverPlate }}</span>
    </div>
  </fieldset>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, Ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { startCase } from 'lodash';

import util from '@/Utils/util';
import { sendGet } from '@/Utils/api';
import type { Ride } from '@/models/ride';
import { commonDateTimeFormat } from '@/constants/app';

type RideHistory = {
  bearing?: number;
  eta?: number;
  requestId?: string;
  latitude?: number;
  longitude?: number;
  status?: string;
}

const activeStatusList: string[] = ['start', 'processing', 'accepted', 'arriving', 'in_progress'];

const router = useRouter();
const route = useRoute();

const props = defineProps({
  rideStatus: {
    type: Object,
    default: () => ({}),
  },
});

const lastRideHistory: Ref<RideHistory> = ref({
  eta: props.rideStatus.eta,
  requestId: props.rideStatus.requestId,
  status: props.rideStatus.rideStatus,
});
const rideInfo: Ref<Ride | null> = ref(null);
const isLoadingRideInfo = ref(false);

const isActiveRide = computed(() => {
  if (!rideInfo.value) {
    return true;
  }

  return activeStatusList.includes(rideInfo.value.status);
});

const etaTxt = computed(() => {
  const status = rideInfo.value?.status ?? lastRideHistory.value.status;

  if (!status) {
    return 'N/A';
  }

  if (status === 'completed') {
    return `Dropped off at ${util.formatDateTime(rideInfo.value?.timeCompleted, commonDateTimeFormat)}`;
  }

  if (status === 'redispatching') {
    return 'Finding a new driver';
  }

  if (!isActiveRide.value) {
    return startCase(status);
  }

  if (['start', 'processing', 'accepted', 'arriving'].includes(status)) {
    return lastRideHistory.value.eta ? `Pickup in ${lastRideHistory.value.eta} minutes` : 'A driver will be assigned shortly';
  }

  if (status === 'in_progress') {
    return `Dropoff in ${lastRideHistory.value.eta} minutes`
  }

  return startCase(status);
});

const getRideHistory = async () => {
  if (!props.rideStatus.requestId) {
    return;
  }

  try {
    const { data } = await sendGet(`${util.historyAPI}/ridehistory/${props.rideStatus.requestId}`);

    if (data.length) {
      lastRideHistory.value = data[data.length - 1];
    }
  } catch (err) {
    // No need to do anything
  }
};

const getRideInfo = async () => {
  if (!props.rideStatus.requestId) {
    return;
  }

  try {
    const query = {
      type: 'request-id',
      q: props.rideStatus.requestId,
    };

    const { data } = await sendGet('rides', query);

    if (data.count > 0) {
      rideInfo.value = data.rows[0];
    }
  } catch (err) {
    // No need to do anything
  }
};

const onRefresh = async () => {
  isLoadingRideInfo.value = true;
  await Promise.all([
    getRideHistory(),
    getRideInfo(),
  ]);
  isLoadingRideInfo.value = false;
};

const gotoRichMap = () => {
  if (!rideInfo.value?.id) {
    return;
  }

  router.push({
    name: 'RideHistory',
    hash: '#ride-activities',
    query: {
      ...route.query,
      rideId: rideInfo.value.id,
    },
  });
};

onMounted(onRefresh);
</script>

<style lang="scss" scoped>
.order-ride-info {
  .action-btn {
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 100%;
    color: #6d509c;
    font-size: 18px;
    cursor: pointer;
  }
}
</style>
